import { EntityModel } from '@/interfaces'
import {
  ALL_TRAVELLERS_QUERY,
  CREATE_TRAVELLER_MUTATION,
  DELETE_TRAVELLER_MUTATION,
  FORCE_DELETE_TRAVELLER_MUTATION,
  SINGLE_TRAVELLER_QUERY,
  UPDATE_TRAVELLER_MUTATION,
} from './graphql'
import travelPricingAgeGroupEntity from '@/entities/travelPricingAgeGroup/entity'
import ratioAccommodationEntity from '@/entities/ratioAccommodation/entity'
import ratioFamilyPackageEntity from '@/entities/ratioFamilyPackage/entity'
import ratioAccommodationAddonEntity from '@/entities/ratioAccommodationAddon/entity'
import ratioPersonalAddonEntity from '@/entities/ratioPersonalAddon/entity'
import ratioBookingAddonEntity from '@/entities/ratioBookingAddon/entity'
import ratioTravelExtensionEntity from '@/entities/ratioTravelExtension/entity'
import passportDataEntity from '@/entities/passportData/entity'

const travellerModel: EntityModel = {
  name: 'traveller',
  namePlural: 'travellers',
  uniqueName: 'traveller',
  uniqueNamePlural: 'travellers',
  path: 'travel/booking/traveller',
  baseUrl: '/travel/booking/travellers',
  translationPrefix: 'entities:traveller.',
  getDisplayName: (values: any) => {
    return `${values.firstName} ${values.lastName}`
  },
  gql: {
    getAll: ALL_TRAVELLERS_QUERY,
    getSingle: SINGLE_TRAVELLER_QUERY,
    create: CREATE_TRAVELLER_MUTATION,
    update: UPDATE_TRAVELLER_MUTATION,
    delete: DELETE_TRAVELLER_MUTATION,
    forceDelete: FORCE_DELETE_TRAVELLER_MUTATION,
  },
  attributes: {
    salutation: {
      name: 'salutation',
      label: 'entities:traveller.attributes.salutation.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'select',
      selectOptions: [
        {
          label: 'entities:traveller.attributes.salutation.selectOptions.MR.label',
          value: 'MR',
        },
        {
          label: 'entities:traveller.attributes.salutation.selectOptions.MRS.label',
          value: 'MRS',
        },
      ],
      validationType: 'string',
      validations: [],
    },
    firstName: {
      name: 'firstName',
      label: 'entities:traveller.attributes.firstName.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    lastName: {
      name: 'lastName',
      label: 'entities:traveller.attributes.lastName.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    birthday: {
      name: 'birthday',
      label: 'entities:traveller.attributes.birthday.label',
      type: 'value',
      dataType: 'Date',
      defaultInput: true,
      inputType: 'date',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    nationality: {
      name: 'nationality',
      label: 'entities:traveller.attributes.nationality.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    email: {
      name: 'email',
      label: 'entities:traveller.attributes.email.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [
        {
          type: 'email',
        },
      ],
    },
    phone: {
      name: 'phone',
      label: 'entities:traveller.attributes.phone.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      maxLength: 25,
      validationType: 'string',
      validations: [],
    },
    address: {
      name: 'address',
      label: 'entities:traveller.attributes.address.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    addressNumber: {
      name: 'addressNumber',
      label: 'entities:traveller.attributes.addressNumber.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    addressAddition: {
      name: 'addressAddition',
      label: 'entities:traveller.attributes.addressAddition.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    zip: {
      name: 'zip',
      label: 'entities:traveller.attributes.zip.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    city: {
      name: 'city',
      label: 'entities:traveller.attributes.city.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    country: {
      name: 'country',
      label: 'entities:traveller.attributes.country.label',
      type: 'value',
      dataType: 'string',
      defaultInput: true,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    ageGroup: {
      name: 'ageGroup',
      label: 'entities:traveller.attributes.ageGroup.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return travelPricingAgeGroupEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    accommodation: {
      name: 'accommodation',
      label: 'entities:traveller.attributes.accommodation.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return ratioAccommodationEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
      ],
    },
    familyPackage: {
      name: 'familyPackage',
      label: 'entities:traveller.attributes.familyPackage.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return ratioFamilyPackageEntity
      },
      defaultInput: false,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
    accommodationAddons: {
      name: 'accommodationAddons',
      label: 'entities:traveller.attributes.accommodationAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioAccommodationAddonEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    personalAddons: {
      name: 'personalAddons',
      label: 'entities:traveller.attributes.personalAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioPersonalAddonEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    bookingAddons: {
      name: 'bookingAddons',
      label: 'entities:traveller.attributes.bookingAddons.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioBookingAddonEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    extensions: {
      name: 'extensions',
      label: 'entities:traveller.attributes.extensions.label',
      type: 'entityCollection',
      dataType: '[any]',
      getEntity: () => {
        return ratioTravelExtensionEntity
      },
      defaultInput: false,
      inputType: 'text',
      validationType: 'string',
      validations: [],
    },
    passportData: {
      name: 'passportData',
      label: 'entities:traveller.attributes.passportData.label',
      type: 'entityObject',
      dataType: 'any',
      getEntity: () => {
        return passportDataEntity
      },
      defaultInput: true,
      inputType: 'select',
      validationType: 'string',
      validations: [],
    },
  },
}

export default travellerModel
